import React, { useEffect, useState, useMemo } from "react";
import { saleReportStyles } from "../../../Pdf/pdfStyles";
import {
  PDFViewer,
  Page,
  Text,
  View,
  Document,
} from "@react-pdf/renderer";
import moment from "moment";
import { Spin } from "antd";
import axios from "axios";
import config from "../../../config";
import PrintHeaderAccount from "../../../Pdf/PrintHeaderAccount";
import PrintFooter from "../../../Pdf/PrintFooter";
import {
  PrintFormateNumber,
  parseReportAmount,
} from "../../../Pdf/PrintFormateNumber";
import { getDecimalPlaces } from "../../Shared/Utility";

const COL = {
  sr: 26,
  code: 48,
  name: 175,
  lastPurchase: 78,
  salePrice: 78,
  qty: 62,
  stockValue: 100,
};

const TABLE_WIDTH =
  COL.sr +
  COL.code +
  COL.name +
  COL.lastPurchase +
  COL.salePrice +
  COL.qty +
  COL.stockValue;

const parseBoolParam = (value) =>
  value === true || value === "true" || value === "1";

const resolveStockDate = (raw) => {
  if (!raw || raw === "null" || raw === "undefined") return null;
  const parsed = moment(raw, ["YYYY-MM-DD", config.date_format], true);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
};

const PrintProductList = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const rawDate = urlParams.get("Date");
  const OrderBy = urlParams.get("OrderBy") || "";
  const HideZeroBalance = parseBoolParam(urlParams.get("HideZeroBalance"));
  const HideNegativeBalance = parseBoolParam(
    urlParams.get("HideNegativeBalance")
  );

  const stockDate = resolveStockDate(rawDate);

  const UserID = localStorage.getItem("ID");
  const AccessKey = localStorage.getItem("AccessKey");

  const [listofProducts, setListofProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalStockValue, setTotalStockValue] = useState(0);

  const priceDecimals = Math.min(getDecimalPlaces(), 6);

  useEffect(() => {
    document.title = "Print Product List";
    if (!UserID || !AccessKey) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const payload = {
      UserID,
      AccessKey,
      OrderBy,
      HideZeroBalance,
      HideNegativeBalance,
    };

    if (stockDate) {
      payload.Dated = stockDate;
    }

    axios({
      method: "post",
      url: config.base_url + "Product/GetProducts",
      headers: { "Content-Type": "application/json" },
      data: payload,
    })
      .then(function (response) {
        if (response.data.status_code == 1) {
          setListofProducts(response.data.ListofProducts || []);
          setTotalStockValue(parseReportAmount(response.data.TotalStockValue));
        } else {
          setListofProducts([]);
          setTotalStockValue(0);
        }
      })
      .catch(function () {
        setListofProducts([]);
        setTotalStockValue(0);
      })
      .finally(function () {
        setLoading(false);
      });
  }, [UserID, AccessKey, stockDate, OrderBy, HideZeroBalance, HideNegativeBalance]);

  const subtitle = useMemo(() => {
    if (stockDate) {
      return `Stock as of ${moment(stockDate).format(config.date_format)}`;
    }
    return "Current stock position";
  }, [stockDate]);

  const renderHeaderCells = () => (
    <>
      <View style={[saleReportStyles.th, { width: COL.sr }]}>
        <Text style={saleReportStyles.thValue}>Sr.#</Text>
      </View>
      <View style={[saleReportStyles.th, { width: COL.code }]}>
        <Text style={saleReportStyles.thValue}>Code</Text>
      </View>
      <View style={[saleReportStyles.th, { width: COL.name }]}>
        <Text style={saleReportStyles.thValue}>Name</Text>
      </View>
      <View
        style={[
          saleReportStyles.th,
          { width: COL.lastPurchase, textAlign: "right" },
        ]}
      >
        <Text style={saleReportStyles.thValue}>Last Purchase Price</Text>
      </View>
      <View
        style={[
          saleReportStyles.th,
          { width: COL.salePrice, textAlign: "right" },
        ]}
      >
        <Text style={saleReportStyles.thValue}>Sale Price</Text>
      </View>
      <View
        style={[saleReportStyles.th, { width: COL.qty, textAlign: "right" }]}
      >
        <Text style={saleReportStyles.thValue}>Current Qty</Text>
      </View>
      <View
        style={[
          saleReportStyles.th,
          { width: COL.stockValue, textAlign: "right" },
        ]}
      >
        <Text style={saleReportStyles.thValue}>Stock Value</Text>
      </View>
    </>
  );

  const generateData = () => {
    if (!listofProducts.length) return null;

    return listofProducts.map((item, index) => {
      const balance = parseReportAmount(item.Balance);
      const lastPurchase = parseReportAmount(item.Last_Purchase_Price);
      const salePrice = parseReportAmount(item.Sale_Price);
      const stockValue = balance * lastPurchase;

      return (
        <View key={item.ID || index} style={saleReportStyles.row}>
          <View style={[saleReportStyles.td, { width: COL.sr }]}>
            <Text style={saleReportStyles.tdValue}>{index + 1}</Text>
          </View>
          <View style={[saleReportStyles.td, { width: COL.code }]}>
            <Text style={saleReportStyles.tdValue}>
              {item.ProductCode || "—"}
            </Text>
          </View>
          <View style={[saleReportStyles.td, { width: COL.name }]}>
            <Text style={saleReportStyles.tdValue}>{item.Name || "—"}</Text>
          </View>
          <View
            style={[
              saleReportStyles.td,
              { width: COL.lastPurchase, textAlign: "right" },
            ]}
          >
            <Text style={saleReportStyles.tdValue}>
              {PrintFormateNumber(lastPurchase, priceDecimals)}
            </Text>
          </View>
          <View
            style={[
              saleReportStyles.td,
              { width: COL.salePrice, textAlign: "right" },
            ]}
          >
            <Text style={saleReportStyles.tdValue}>
              {PrintFormateNumber(salePrice, priceDecimals)}
            </Text>
          </View>
          <View
            style={[
              saleReportStyles.td,
              { width: COL.qty, textAlign: "right" },
            ]}
          >
            <Text style={saleReportStyles.tdValue}>
              {PrintFormateNumber(balance)}
            </Text>
          </View>
          <View
            style={[
              saleReportStyles.td,
              { width: COL.stockValue, textAlign: "right" },
            ]}
          >
            <Text style={saleReportStyles.tdValue}>
              {PrintFormateNumber(stockValue, 0)}
            </Text>
          </View>
        </View>
      );
    });
  };

  const MyDocument = () => (
    <Document>
      <Page size="A4" style={saleReportStyles.page}>
        <PrintHeaderAccount compact />
        <View style={saleReportStyles.section}>
          <View style={saleReportStyles.titleBlock}>
            <Text style={saleReportStyles.pagetitle}>Stock Details</Text>
            <Text style={saleReportStyles.reportSubtitle}>{subtitle}</Text>
            {OrderBy ? (
              <Text style={saleReportStyles.reportSubtitle}>
                Sorted by: {OrderBy}
              </Text>
            ) : null}
          </View>

          <View style={[saleReportStyles.table, { width: TABLE_WIDTH }]}>
            <View style={saleReportStyles.tableHead}>{renderHeaderCells()}</View>
            {generateData()}
            <View style={saleReportStyles.totalsRow}>
              <View
                style={[
                  saleReportStyles.th,
                  {
                    width: TABLE_WIDTH - COL.stockValue,
                    textAlign: "right",
                  },
                ]}
              >
                <Text style={saleReportStyles.thValue}>Total Stock Value</Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: COL.stockValue, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>
                  {PrintFormateNumber(totalStockValue, 0)}
                </Text>
              </View>
            </View>
          </View>
        </View>
        <PrintFooter fixed />
      </Page>
    </Document>
  );

  return (
    <div style={{ width: "100%", height: "100vh", overflow: "hidden" }}>
      {loading ? (
        <div
          style={{
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spin />
        </div>
      ) : (
        <PDFViewer width="100%" height="100%">
          <MyDocument />
        </PDFViewer>
      )}
    </div>
  );
};

export default PrintProductList;
